// components/ui/Modal.jsx
// Created: 2026-07-20
// Project: Giving Words to Voices
// Version: 1.1.0
//
// The Modal primitive — the centred-overlay convention, once. The app's modals
// each re-authored the same shell: a fixed full-screen backdrop, a centred
// card on theme.sidebar, click-outside to close. This owns that shell so a
// modal is a title, a body, and an optional footer.
//
// Two layouts:
//  • Simple (default) — a padded card: optional title row, children, footer.
//  • Regions — pass a `header` node and the card becomes a three-region shell:
//    a bordered header (icon + title + controls, close appended), an
//    independently-scrolling body, and a bordered footer. This is what the
//    app's rich modals (export pickers, media players, panels) hand-rolled.
//
// It also adds what the inline versions mostly lacked: Escape closes it, the
// dialog carries role="dialog"/aria-modal, and there is a real close control.
//
// z-index: defaults to Z.modal from the token ladder, but takes an explicit
// override. Dialogs that must sit above ordinary modals pass zIndex={Z.dialog}.

import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { X } from 'lucide-react';
import { RADIUS, SPACE, Z, WEIGHT, elevation, isDarkTheme } from '../../constants/tokens';
import { Icon } from '../Icon';

export default function Modal({
  open,
  onClose,
  title,
  children,
  footer = null,
  header = null,          // regions layout: bordered header node (close is appended)
  width = 420,
  maxHeight = '90vh',
  closeOnBackdrop = true,
  showClose = true,
  zIndex,                 // override the Z.modal default (e.g. Z.dialog)
  theme = {},
  bodyStyle = {},
  backdropStyle = {},     // override backdrop (e.g. a darker scrim for media)
}) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose && onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const isDark = isDarkTheme(theme);
  const regions = header != null;

  const closeBtn = showClose && onClose ? (
    <button
      type="button"
      aria-label="Close"
      onClick={onClose}
      style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', padding: 0, lineHeight: 1, flexShrink: 0 }}
    >
      <Icon icon={X} size={18} color={theme.textSub} />
    </button>
  ) : null;

  const card = regions ? (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={typeof title === 'string' ? title : undefined}
      style={{
        width,
        maxWidth: '100%',
        maxHeight,
        background: theme.sidebar || theme.card,
        border: `1px solid ${theme.border}`,
        borderRadius: RADIUS.lg,
        boxShadow: elevation(isDark).lg,
        color: theme.text,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: SPACE[3], padding: `${SPACE[3]} ${SPACE[4]}`, borderBottom: `1px solid ${theme.border}`, flexShrink: 0 }}>
        {header}
        {closeBtn}
      </div>
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: SPACE[4], ...bodyStyle }}>
        {children}
      </div>
      {footer && (
        <div style={{ display: 'flex', gap: SPACE[2], padding: `${SPACE[3]} ${SPACE[4]}`, borderTop: `1px solid ${theme.border}`, justifyContent: 'flex-end', alignItems: 'center', flexShrink: 0 }}>
          {footer}
        </div>
      )}
    </div>
  ) : (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={typeof title === 'string' ? title : undefined}
      style={{
        width,
        maxWidth: '100%',
        maxHeight,
        overflowY: 'auto',
        background: theme.sidebar || theme.card,
        border: `1px solid ${theme.border}`,
        borderRadius: RADIUS.lg,
        padding: SPACE[5],
        boxShadow: elevation(isDark).lg,
        color: theme.text,
        ...bodyStyle,
      }}
    >
      {(title || (showClose && onClose)) && (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: SPACE[3], marginBottom: SPACE[2] }}>
          {title && <div style={{ fontWeight: WEIGHT.bold, color: theme.text }}>{title}</div>}
          {closeBtn}
        </div>
      )}
      {children}
      {footer && (
        <div style={{ display: 'flex', gap: SPACE[2], marginTop: SPACE[3], justifyContent: 'flex-end', alignItems: 'center' }}>
          {footer}
        </div>
      )}
    </div>
  );

  return ReactDOM.createPortal(
    <div
      role="presentation"
      onClick={(e) => { if (closeOnBackdrop && e.target === e.currentTarget) onClose && onClose(); }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.6)',
        zIndex: zIndex != null ? zIndex : Z.modal,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: SPACE[4],
        ...backdropStyle,
      }}
    >
      {card}
    </div>,
    document.body,
  );
}
